import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Paginator } from '../../components/molecules/Paginator'
import { TableShell } from '../../components/molecules/TableShell'
import { Th, Td } from '../../components/molecules/Table'
import {
  useOrderManagementBuildRequests,
  type BuildRequestListDto
} from '../../../viewmodels/useOrderManagementBuildRequests'
import { BuildRequestEditModal } from './BuildRequestEditModal'

/**
 * /order-management/build-requests — operator review of every active
 * customer build_request.
 *
 * Flat table — one row per request. Inline action visibility by status:
 *   requested → Edit + Cancel
 *   confirmed → Cancel + Publish (Edit hidden — the row is locked
 *               in-system once the operator commits via the modal's
 *               Confirm action)
 *   built     → Edit (read-only history + Decommission inside the
 *               modal) + Re-attach URL (recovery for the rare case
 *               that the CF binding goes missing)
 *   cancelled → Edit (history view only)
 *
 * Publish derives the subdomain slug from the assigned organisation's
 * name and provisions the tenant via the per-tenant Cloudflare custom
 * domain attach.
 */
export function BuildRequestsPage(): JSX.Element {
  const vm = useOrderManagementBuildRequests()
  const [openId, setOpenId] = useState<string | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [publishingId, setPublishingId] = useState<string | null>(null)
  const [reattachingId, setReattachingId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)
  const [rowOk, setRowOk] = useState<string | null>(null)

  const cancelRow = async (r: BuildRequestListDto): Promise<void> => {
    if (typeof window !== 'undefined') {
      const ok = window.confirm(
        `Cancel the build request from ${r.customer_email}? This cannot be undone.`
      )
      if (!ok) return
    }
    setCancellingId(r.id)
    setRowError(null)
    const res = await vm.cancel(r.id)
    setCancellingId(null)
    if (!res.ok) setRowError(res.message)
  }

  const publishRow = async (r: BuildRequestListDto): Promise<void> => {
    if (typeof window !== 'undefined') {
      const ok = window.confirm(
        `Publish the subdomain for ${r.organisation_name}? ` +
        `This is non-reversible (the submitter gets reassigned to the new org).`
      )
      if (!ok) return
    }
    setPublishingId(r.id)
    setRowError(null)
    setRowOk(null)
    const res = await vm.publish(r.id)
    setPublishingId(null)
    if (!res.ok) setRowError(res.message)
  }

  const reattachRow = async (r: BuildRequestListDto): Promise<void> => {
    if (typeof window !== 'undefined') {
      const host = r.subdomain !== null ? `${r.subdomain}.uyyamadilu.com` : '(unknown)'
      const ok = window.confirm(
        `Re-attach the Cloudflare custom domain for ${host}? ` +
        `Use this when the URL stops resolving even though the row is 'built'.`
      )
      if (!ok) return
    }
    setReattachingId(r.id)
    setRowError(null)
    setRowOk(null)
    const res = await vm.reattach(r.id)
    setReattachingId(null)
    if (res.ok) {
      setRowOk(
        r.subdomain !== null
          ? `Re-attached ${r.subdomain}.uyyamadilu.com — give the edge ~1 minute.`
          : 'Re-attached.'
      )
    } else {
      setRowError(res.message)
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-h2 text-charcoal">Build Requests</h1>
        <p className="font-body text-body-sm text-text-secondary mt-1">
          Customer build submissions awaiting review. Edit to assign an organisation,
          set the max allowed count, adjust line quantities, and walk through PO received → Built.
        </p>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button type="button" className="underline underline-offset-2 hover:text-charcoal" onClick={vm.reload}>
            Retry
          </button>
        </Alert>
      ) : null}
      {rowError !== null ? <Alert tone="error">{rowError}</Alert> : null}
      {rowOk !== null ? <Alert tone="info">{rowOk}</Alert> : null}

      {vm.isLoading && vm.requests.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : vm.requests.length === 0 ? (
        <Placeholder>No build requests yet — they show up here as customers submit them from /build.</Placeholder>
      ) : (
        <>
          <TableShell>
            <table className="w-full font-body text-body-sm">
              <thead className="bg-parchment/50 text-text-secondary">
                <tr>
                  <Th>Customer</Th>
                  <Th>Email</Th>
                  <Th>Phone</Th>
                  <Th>Organisation</Th>
                  <Th className="text-right">Items</Th>
                  <Th className="text-right">Max allowed</Th>
                  <Th className="text-right">Max cart (₹)</Th>
                  <Th>Status</Th>
                  <Th>Requested</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {vm.requests.map((r) => (
                  <BuildRequestRow
                    key={r.id}
                    request={r}
                    onOpen={() => setOpenId(r.id)}
                    onCancel={() => void cancelRow(r)}
                    onPublish={() => void publishRow(r)}
                    onReattach={() => void reattachRow(r)}
                    cancelling={cancellingId === r.id}
                    publishing={publishingId === r.id}
                    reattaching={reattachingId === r.id}
                  />
                ))}
              </tbody>
            </table>
          </TableShell>
          <Paginator
            page={vm.page}
            pageSize={vm.pageSize}
            total={vm.total}
            onPageChange={vm.setPage}
            disabled={vm.isLoading}
          />
        </>
      )}

      {openId !== null ? (
        <BuildRequestEditModal
          id={openId}
          onClose={(refresh) => {
            setOpenId(null)
            if (refresh) vm.reload()
          }}
        />
      ) : null}
    </div>
  )
}

/** Render the Customer cell — full name when on file; otherwise fall
 *  back to the email-local-part in muted italic so the column still has
 *  a row identifier (the full email is in the next column already, so
 *  showing it whole would just duplicate). Mirrors the firstName ?? email
 *  pattern used on the Account dashboard. */
function CustomerCell({ request }: { readonly request: BuildRequestListDto }): JSX.Element {
  const name = [request.customer_first_name, request.customer_last_name].filter(Boolean).join(' ')
  if (name.length > 0) return <>{name}</>
  const at = request.customer_email.indexOf('@')
  const local = at > 0 ? request.customer_email.slice(0, at) : request.customer_email
  return <span className="italic text-text-secondary">{local}</span>
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

/** Format paisa as a ₹-prefixed rupee figure with Indian grouping
 *  + 2 decimals. Mirrors the formatRupees used in the cart drawer so
 *  every operator-facing money cell reads consistently. */
function formatRupees(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function statusLabel(status: string): string {
  switch (status) {
    case 'requested': return 'Requested'
    case 'confirmed': return 'Confirmed'
    case 'built': return 'Built'
    case 'cancelled': return 'Cancelled'
    default: return status
  }
}

function statusClasses(status: string): string {
  switch (status) {
    case 'requested': return 'bg-bg-secondary text-text-secondary'
    case 'confirmed': return 'bg-forest/15 text-forest'
    case 'built':     return 'bg-forest/25 text-forest'
    case 'cancelled': return 'bg-terra/15 text-terra'
    default:          return 'bg-bg-secondary text-text-secondary'
  }
}

function BuildRequestRow({
  request,
  onOpen,
  onCancel,
  onPublish,
  onReattach,
  cancelling,
  publishing,
  reattaching
}: {
  readonly request: BuildRequestListDto
  readonly onOpen: () => void
  readonly onCancel: () => void
  readonly onPublish: () => void
  readonly onReattach: () => void
  readonly cancelling: boolean
  readonly publishing: boolean
  readonly reattaching: boolean
}): JSX.Element {
  // Action visibility by status (Kiran 2026-06-08):
  //   requested  → Edit + Cancel
  //   confirmed  → Cancel + Publish (NO Edit — the row is locked
  //                in-system once confirmed; backend still validates
  //                org + max_allowed_count are set before provisioning)
  //   built      → Edit (read-only view + Decommission inside)
  //   cancelled  → Edit (history view only)
  // Edit is hidden specifically on 'confirmed' so the operator's
  // attention goes to the one forward action they should take.
  const canCancel = request.status === 'requested' || request.status === 'confirmed'
  const canPublish = request.status === 'confirmed'
  const canEdit = request.status !== 'confirmed'
  // Re-attach is a recovery affordance for 'built' rows whose Cloudflare
  // custom-domain binding is missing (silent PUT failure, accidental
  // detach). The backend rejects re-attach on any other status, so we
  // simply gate by 'built'. Kiran 2026-06-08.
  const canReattach = request.status === 'built'
  return (
    <tr className="border-t border-border/40">
      <Td><CustomerCell request={request} /></Td>
      <Td className="font-mono text-body-xs">{request.customer_email}</Td>
      <Td className="font-mono text-body-xs">{request.customer_phone ?? '—'}</Td>
      <Td>{request.organisation_name}</Td>
      <Td className="text-right whitespace-nowrap font-mono">{request.item_count}</Td>
      <Td className="text-right whitespace-nowrap font-mono">
        {request.max_allowed_count !== null ? request.max_allowed_count : '—'}
      </Td>
      <Td className="text-right whitespace-nowrap font-mono">
        {request.max_cart_value_paisa !== null ? formatRupees(request.max_cart_value_paisa) : '—'}
      </Td>
      <Td>
        <span className={`inline-block rounded-full px-2 py-0.5 font-body text-body-xs ${statusClasses(request.status)}`}>
          {statusLabel(request.status)}
        </span>
      </Td>
      <Td className="whitespace-nowrap text-text-secondary">{formatDate(request.requested_at)}</Td>
      <Td className="text-right whitespace-nowrap">
        {canEdit ? (
          <Button variant="ghost" size="sm" onClick={onOpen}>Edit</Button>
        ) : null}
        {canCancel ? (
          <>
            {canEdit ? ' ' : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={cancelling || publishing}
            >
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </Button>
          </>
        ) : null}
        {canPublish ? (
          <>
            {' '}
            <Button
              variant="primary"
              size="sm"
              onClick={onPublish}
              disabled={publishing || cancelling}
            >
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          </>
        ) : null}
        {canReattach ? (
          <>
            {' '}
            <Button
              variant="ghost"
              size="sm"
              onClick={onReattach}
              disabled={reattaching}
              title="Re-attach the Cloudflare custom domain (use if the URL stopped resolving)"
            >
              {reattaching ? 'Re-attaching…' : 'Re-attach URL'}
            </Button>
          </>
        ) : null}
      </Td>
    </tr>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}
